<template>
  <div id="interactive-map" :style="`height: ${height}`">
    <MapOverlays :map="interactiveMap" :autoShowSpecies="isLocationMap" ref="mapOverlays" @state="overlayState = $event" @toggle-species="toggleSpeciesLayer" />
    <v-row class="map-button-row-top" :class="{ 'map-button-row-top--passthrough': layersCollapsible }" dense>
      <v-col v-if="!(includeSearchByTract && layersCollapsible)" cols="auto">
        <v-card v-if="showCoordinates" :color="SOFT_BLACK" class="mt-2">
          <v-card-text class="white--text body-2 py-2 px-3">
            <v-row dense>
              <v-col cols="auto">
                <span>{{ ticket?.lastKnownLatitude.toFixed(6) }},</span>
              </v-col>
              <v-col cols="auto">
                <span> {{ ticket?.lastKnownLongitude.toFixed(6) }}</span>
              </v-col>
              <v-col cols="auto">
                <Icon
                margin="ml-1"
                iconColor="white"
                icon="mdi-content-copy"
                tooltipColor="black"
                :tooltipText="$t('copyToClipboard')"
                @icon-clicked="copyLastKnownLocationToClipboard"></Icon>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <Icon
        v-if="includeSearchByTract"
        icon="mdi-magnify"
        @icon-clicked="searchEnabled = !searchEnabled"
        :tooltipText="searchIconTooltip"
        xLarge
        :small="false"
        iconColor="white"
        margin="mr-2 map-option-button"
        tooltipColor="black"
        />

        <Icon
        v-if="$vuetify.breakpoint.smAndDown && !tickets && !layersCollapsible"
        class="mr-0 map-option-button"
        icon="mdi-cloud"
        @icon-clicked="toggleWeatherOverlay"
        :tooltipText="weatherToggleTooltip"
        xLarge
        :small="false"
        iconColor="white"
        tooltipColor="black"
        />
      </v-col>
      <v-col v-if="includeSearchByTract" style="min-width:200px;" md="8">
        <TractAutocomplete
        :class="searchEnabled ? 'visible in-front' : 'invisible behind'"
        data-testid="contract-tract"
        id="map-search-text"
        :clearable="true"
        :propTractId="this.tract?.tractId"
        @tract-chosen="tractChosen"
        @tract-cleared="tractCleared"
        :showLabel=false
        :propFilter="propFilter"
        :staleSearch="focusedTractNotInList ? this.tract : undefined"
        soloTheme
        />
      </v-col>
      <v-spacer v-if="!$vuetify.breakpoint.smAndDown"/>
      <v-col v-if="tickets && transitStatusFilter" cols="auto">
        <v-menu
          v-model="transitStatusFilterOpen"
          :close-on-content-click="false"
          right
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
            class="map-option-button"
            v-on="on"
            v-bind="attrs"
            x-large
            color="white"
          >
            mdi-filter
          </v-icon>
          </template>
          <v-card>
            <v-card-title class="primary white--text">
              {{$t('ticketTransitStatusFilter')}}
            </v-card-title>
            <v-card-text>
              <v-container fluid>
                <v-row dense>
                  <v-col v-for="ts in transitStatus" :key="`status-${ts.label}`" cols="12" class="ma-0 pa-0">
                    <v-checkbox :label="getCheckboxLabel(ts.label, ts.value)" v-model="transitStatusFilter" :value="ts.value"/>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-menu>
      </v-col>
      <v-col cols="auto" :class="{ 'd-flex flex-column': layersVertical, 'align-start': layersVertical && !includeSearchByTract, 'align-center': layersVertical && includeSearchByTract, 'order-first': layersCollapsible && includeSearchByTract }">
        <Icon
        v-if="includeSearchByTract && layersCollapsible"
        icon="mdi-magnify"
        @icon-clicked="searchEnabled = !searchEnabled"
        :tooltipText="searchIconTooltip"
        xLarge
        :small="false"
        iconColor="white"
        :margin="layersVertical ? 'map-option-button' : 'mr-2 map-option-button'"
        tooltipColor="black"
        />
        <div v-if="layersCollapsible" class="map-overlay-toggle-group">
          <Icon
          :icon="layersToggleIcon"
          @icon-clicked="layersOpen = !layersOpen"
          :tooltipText="$t('mapLayers')"
          xLarge
          :small="false"
          :iconColor="layersOpen ? 'primary' : 'grey darken-2'"
          tooltipColor="black"
          margin="map-option-button"
          elementId="map-layers-toggle"
          />
        </div>
        <div
        v-show="!layersCollapsible || layersOpen"
        class="map-overlay-toggle-group"
        :class="{ 'map-overlay-toggle-group--vertical': layersVertical, 'ml-2': layersCollapsible && !layersVertical }"
        @mouseenter="hideMapTips"
        @click="layersOpen = false">
          <Icon
          v-if="userPosition"
          icon="mdi-map-marker-account"
          @icon-clicked="centerOnUserLocation"
          :tooltipText="$t('centerOnMyLocation')"
          xLarge
          :small="false"
          iconColor="grey darken-2"
          tooltipColor="black"
          margin="map-option-button"
          elementId="center-on-user-location"
          />
          <Icon
          v-if="(!$vuetify.breakpoint.smAndDown || layersCollapsible) && !tickets"
          icon="mdi-cloud"
          @icon-clicked="toggleWeatherOverlay"
          :tooltipText="weatherToggleTooltip"
          xLarge
          :small="false"
          :iconColor="currentWeatherOverlay ? 'primary' : 'grey darken-2'"
          tooltipColor="black"
          margin="map-option-button"
          elementId="weather-overlay-toggle"
          />
          <Icon
          v-if="isLocationMap"
          icon="mdi-land-plots-marker"
          @icon-clicked="toggleNearbyParcels"
          :tooltipText="$t(overlayState.nearbyParcels.active ? 'hideNearbyParcels' : 'showNearbyParcels')"
          xLarge
          :small="false"
          :iconColor="overlayState.nearbyParcels.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="parcel-tiles-toggle"
          />
          <Icon
          v-if="isLocationMap || ticket"
          icon="mdi-crosshairs-gps"
          @icon-clicked="showNearbyTracts = !showNearbyTracts"
          :tooltipText="showNearbyTractsTooltip"
          xLarge
          :small="false"
          :iconColor="showNearbyTracts ? 'primary' : 'grey darken-2'"
          tooltipColor="black"
          margin="map-option-button"
          elementId="show-nearby-tracts-toggle"
          />
          <Icon
          v-if="isLocationMap && showNearbyTracts"
          icon="mdi-vector-polygon"
          @icon-clicked="toggleNearbyBoundaries"
          :tooltipText="$t('nearbyBoundaries')"
          xLarge
          :small="false"
          :iconColor="overlayState.nearbyBoundaries.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="nearby-boundaries-toggle"
          />
          <Icon
          v-if="isLocationMap && overlayState.species.canShow"
          icon="mdi-paw"
          @icon-clicked="toggleSpeciesLayer"
          :tooltipText="$t('speciesLayer')"
          xLarge
          :small="false"
          :iconColor="overlayState.species.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="species-layer-toggle"
          />
          <Icon
          v-if="isLocationMap && companyInfo.hasCustomMapLayers"
          icon="mdi-layers-outline"
          @icon-clicked="toggleCustomLayers"
          :tooltipText="$t('customLayers')"
          xLarge
          :small="false"
          :iconColor="overlayState.custom.active || overlayState.custom.open ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="custom-layers-toggle"
          />
          <Icon
          v-if="isLocationMap"
          icon="mdi-image-filter-hdr"
          @icon-clicked="toggleTopo"
          :tooltipText="$t('topoLayer')"
          xLarge
          :small="false"
          :iconColor="overlayState.topo.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="topo-layer-toggle"
          />
          <Icon
          v-if="isLocationMap"
          icon="mdi-tractor"
          @icon-clicked="analyzeSoil"
          :tooltipText="$t('analyzeOperability')"
          xLarge
          :small="false"
          :iconColor="overlayState.soil.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="soil-map-units-toggle"
          />
          <Icon
          v-if="showAccountLayer"
          icon="mdi-domain"
          @icon-clicked="toggleAccountLayer"
          :tooltipText="$t('accountLocations')"
          xLarge
          :small="false"
          :iconColor="showingAccountLayer ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="account-layer-toggle"
          />
          <div class="map-option-button" v-if="includeSearchByTract" @click.stop>
            <TractFilter
            appendElementId="tract-map"
            :filter="propFilter"
            @filter-applied="applyFilter"
            xLarge
            iconColor="grey darken-2"
            tooltipColor="black"
            />
          </div>
        </div>
      </v-col>
      <v-spacer v-if="$vuetify.breakpoint.smAndDown && !includeSearchByTract"/>
      <v-col v-if="$vuetify.breakpoint.smAndDown && !isLocationMap" cols="auto">
        <div
        class="map-option-button"
        >
          <Icon
          class="mr-0"
          icon="mdi-close"
          @icon-clicked="$emit('map-closed')"
          :tooltipText="$t('close')"
          xLarge
          :small="false"
          iconColor="white"
          tooltipColor="black"
          />
        </div>
      </v-col>
    </v-row>

    <v-card :style="legend.containerStyle" v-if="$vuetify.breakpoint.mdAndUp && interactiveMap && legend" @mouseenter="hideMapTips">
      <v-col
      v-for="(legendItem) in legendContent"
      :key="legendItem.label"
      :style="{paddingTop: '4px', cursor: legendItem.label === 'parcel' ? 'pointer' : ''}"
      @click="toggleLegendItemVisibility(legendItem)"
      >
        <v-row dense align="center" :id="legendItem.elementId">
          <v-col cols="auto">
            <v-icon
            color="white"
            small
            v-if="legendItem.hidden">
              mdi-eye-off
            </v-icon>
            <div
            v-else
            :style="legendItem.style">
            </div>
          </v-col>
          <v-col cols="auto">
            <span class="caption white--text">{{ $t(legendItem.label) }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-card>

    <v-tooltip bottom color="black">
      <template
        v-slot:activator="{on}">
        <v-btn
          class="scale-bar-toggle-button"
          v-on="on"
          :ripple="false"
          elevation="0"
          color="#00000000"
          @click="toggleScaleBar()">
        </v-btn>
      </template>
      <span class="subtitle-1 white--text" style="white-space: pre-line">{{ scaleBarToggleTooltip }}</span>
    </v-tooltip>

    <div v-if="tractMarkersHidden" class="map-overlay-warning">
      {{ $t('zoomInToViewTracts') }}
    </div>
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import mapMarkers from '@/images/markers/markers.js'
import { accountsWithReceiverLocation, createAccountLocationMarker } from '@/utils/map/AccountMarkers.js'
import { milesBetween } from '@/utils/Distance.js'
import { TicketStatus, TransitStatus } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { mapActions, mapGetters } from 'vuex'
import { TractFilter } from '@/model/Tract.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { harvestedProgress, tractMarkerSvg, tractPopup, viewportWidthMiles, MAX_VIEWPORT_MILES_FOR_TRACT_MARKERS } from '@/utils/map/TractMarkers.js'
import {
  addMapControlScript,
  removeMapControlScript,
  mapResizeTimeout,
  createPolygonLayersForDataSource,
  PolygonStyle,
  zoomToFeature,
  azureMapsApiKey,
  disposeMap
} from '@/utils/map/MapHelpers'
import { angelGeojsonClient, AngelGeojsonLayer } from '@/utils/map/Geojson.js'
import { overlaysStack } from '@/utils/map/overlayLayers.js'
import { getLegendInstance } from '@/utils/map/Legend'

const defaultCenterCoords = [-80, 39]
const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels
const DEFAULT_ZOOM_LEVEL = 10
const SOFT_BLACK = 'rgba(34, 34, 34, 0.867)'
const USER_LOCATION_COLOR = '#1A73E8'

export default {
  name: 'InteractiveMap',

  components: {
    TractAutocomplete: () => import('@/components/autocomplete/TractAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TractFilter: () => import('@/components/tract/TractFilter.vue'),
    MapOverlays: () => import('@/components/maps/MapOverlays.vue')
  },

  props: {
    tracts: { type: Array, required: false, default: undefined },
    tickets: { type: Array, required: false, default: undefined },
    ticket: { type: Object, required: false, default: undefined },
    height: { type: String, required: false, default: '80vh' },
    includeSearchByTract: { type: Boolean, required: false, default: false },
    propFilter: { type: Object, required: false, default: undefined },
    isLocationMap: { type: Boolean, required: false, default: false },
    containerId: { type: String, required: false, default: undefined },
    showAccountLayer: { type: Boolean, required: false, default: false },
    includeTractDetailLink: { type: Boolean, required: false, default: false },
    showUserLocation: { type: Boolean, required: false, default: false }
  },

  data: () => ({
    SOFT_BLACK,
    interactiveMap: null,
    overlayState: { species: { canShow: false, active: false }, topo: { active: false }, soil: { active: false }, custom: { active: false, open: false }, nearbyTracts: { active: false }, nearbyBoundaries: { active: false }, nearbyParcels: { active: false } },
    openMarker: {
      justAdded: false,
      marker: null
    },
    searchEnabled: true,
    tractId: null,
    tract: null,
    currentWeatherOverlay: undefined,
    weatherLayer: undefined,
    scaleBarRendered: false,
    currentScaleBar: 'imperial',
    scaleBarImport: undefined,
    scaleBarOptions: ['imperial', 'metric'],
    showNearbyTracts: false,
    nearbyTractMarkers: [],
    showingAccountLayer: false,
    accountLocations: [],
    accountMarkers: [],
    transitStatusFilterOpen: false,
    transitStatusFilter: [TransitStatus.Ready.value, TransitStatus.Paused.value],
    tractMarkersHidden: false,

    angelGeojson: null,
    cutLayers: null,
    cutSource: null,
    parcelLayers: null,
    parcelSource: null,
    legend: null,

    userLocationSource: null,
    userLocationWatchId: null,
    userPosition: null,
    userLocationErrorShown: false,
    layersOpen: false
  }),

  watch: {
    tractId (val) {
      if (val) {
        this.interactiveMap.popups.clear()
        this.resetMap()
      } else {
        this.resetMap(false)
      }
    },

    tracts (val, oldVal) {
      const nothingWasShown = !oldVal?.length && !this.tract && !this.ticket
      this.resetMap(nothingWasShown, false)
    },

    showNearbyTracts (val) {
      if (val === false) {
        this.interactiveMap.popups.clear()
        if (this.$refs.mapOverlays) this.$refs.mapOverlays.hideNearbyTractBoundaries()
      }
      this.resetMap(false, false)
    },

    transitStatusFilter () {
      this.resetMap()
    },

    '$vuetify.breakpoint.height' () { this.onResize() },
    '$vuetify.breakpoint.width' () { this.onResize() },
    'topDialogState.fullscreen' () { this.onResize() }
  },

  created () {
    this.zoomGatedTracts = []
    this.renderedTractMarkers = new Map()
  },

  mounted () {
    this.$nextTick(_ => {
      this.initMap()
      this.setupMap(true)
      addMapControlScript()
    })
  },

  beforeDestroy () {
    if (this.userLocationWatchId !== null) navigator.geolocation.clearWatch(this.userLocationWatchId)
    if (this.interactiveMap) disposeMap(this.interactiveMap)
    this.interactiveMap = null
    removeMapControlScript()
  },

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('tract-hud', ['allTractHudSections']),
    searchIconTooltip () {
      return this.$t(this.searchEnabled ? 'hideSearchBar' : 'showSearchBar')
    },
    focusedTractNotInList () {
      return this.tract && this.tractId && !this.tracts.includes(this.tract)
    },
    weatherLayerName () {
      return this.currentWeatherOverlay ? `microsoft.weather.${this.currentWeatherOverlay}.main` : undefined
    },
    weatherToggleTooltip () {
      return this.$t('currentWeatherOverlay', { type: this.$t(this.currentWeatherOverlay || 'none') })
    },
    scaleBarToggleTooltip () {
      return this.$t('changeToUnits', { units: this.$t(this.currentScaleBar === 'imperial' ? 'metric' : 'imperial') })
    },
    showNearbyTractsTooltip () {
      return this.$t('toggleNearbyTracts', { toggle: this.$t(this.showNearbyTracts ? 'hide' : 'show') })
    },
    transitStatus () {
      return [TransitStatus.Ready, TransitStatus.Paused]
    },
    legendContent () {
      return this.legend?.content
    },
    layersCollapsible () {
      return (this.isLocationMap || this.includeSearchByTract) && this.$vuetify.breakpoint.smAndDown
    },
    layersVertical () {
      return this.layersCollapsible && this.$vuetify.breakpoint.height >= this.$vuetify.breakpoint.width
    },
    layersToggleIcon () {
      if (this.layersVertical) return this.layersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'
      return this.layersOpen ? 'mdi-chevron-left' : 'mdi-chevron-right'
    },
    showCoordinates () {
      return !!this.ticket && !!this.ticket.lastKnownLatitude && !!this.ticket.lastKnownLongitude
    }
  },

  methods: {
    ...mapActions('tract', ['fetchTracts', 'fetchTractsOnce']),
    ...mapActions('tract-hud', ['fetchTractHudSections']),
    ...mapActions('account', ['fetchAccounts']),
    resetMap (changeZoom = true, animateSearched = true) {
      this.interactiveMap.markers.clear()
      this.renderedTractMarkers.clear()
      this.zoomGatedTracts = []
      this.$nextTick(_ => {
        this.setupMap(false, changeZoom, animateSearched)
      })
      this.onResize()
    },

    initMap () {
      const mapOptions = {
        center: defaultCenterCoords,
        zoom: DEFAULT_ZOOM_LEVEL,
        view: 'Auto',
        style: 'satellite_road_labels',
        authOptions: {
          authType: 'subscriptionKey',
          subscriptionKey: azureMapsApiKey()
        }
      }

      this.interactiveMap = new atlas.Map('interactive-map', mapOptions)
      this.interactiveMap.events.add('moveend', this.syncTractMarkers)
      if (this.showUserLocation && navigator.geolocation) this.interactiveMap.events.add('ready', this.initUserLocation)
    },

    initUserLocation () {
      this.userLocationSource = new atlas.source.DataSource()
      this.interactiveMap.sources.add(this.userLocationSource)
      this.interactiveMap.layers.add(new atlas.layer.BubbleLayer(this.userLocationSource, null, {
        color: USER_LOCATION_COLOR,
        strokeColor: '#FFFFFF',
        strokeWidth: 3,
        radius: 8
      }))
      this.userLocationWatchId = navigator.geolocation.watchPosition(this.onUserPosition, this.onUserPositionError, { enableHighAccuracy: true })
    },

    onUserPosition ({ coords }) {
      this.userPosition = [coords.longitude, coords.latitude]
      this.userLocationSource.setShapes([new atlas.data.Point(this.userPosition)])
    },

    onUserPositionError (error) {
      if (this.userLocationErrorShown) return
      this.userLocationErrorShown = true
      this.setSnackError(this.$t('errorGettingLocationMessage', { message: error.message }))
    },

    centerOnUserLocation () {
      this.interactiveMap.setCamera({ center: this.userPosition, type: 'fly', duration: 500 })
    },

    async setupMap (addClickListener, changeZoom = true, animateSearched = true) {
      if ((this.tracts || this.showNearbyTracts) && !this.allTractHudSections) {
        try {
          await this.fetchTractHudSections()
        } catch (e) {
          console.error(e)
        }
      }
      const tractsSortedByLatitude = this.tracts || this.showNearbyTracts ? await this.getTractsToMark() : undefined
      const ticketsSortedByLatitude = this.tickets ? await this.getTicketsToMark() : undefined
      if (!this.interactiveMap) return

      this.interactiveMap.events.add('ready', async () => {
        if (this.tracts || this.showNearbyTracts) {
          this.zoomGatedTracts = tractsSortedByLatitude.filter(t => t.tractId !== this.tractId)
          const focusedTracts = tractsSortedByLatitude.filter(t => t.tractId === this.tractId)
          const focusedMarkers = this.createTractMarkers(focusedTracts, animateSearched && !this.isLocationMap)
          this.addMarkers(focusedMarkers)
          focusedMarkers.forEach(marker => { marker.getElement().style.zIndex = 1 })
        } else {
          this.zoomGatedTracts = []
        }

        if (this.tickets) {
          const ticketMarkers = this.createTicketMarkers(ticketsSortedByLatitude)
          this.addMarkers(ticketMarkers)
        }

        if (this.ticket) {
          const ticketMarkers = this.createTicketMarkers()
          this.addMarkers(ticketMarkers)
        }

        if (this.showingAccountLayer) {
          this.renderAccountMarkers()
        }

        await this.handleAngelGeojson()

        if (addClickListener) {
          this.interactiveMap.events.add('click', () => {
            if (this.openMarker.marker !== null && this.openMarker.justAdded === false && this.openMarker.marker.options.popup) {
              this.openMarker.marker.options.popup.remove()
              this.openMarker.marker = null
              this.openMarker.justAdded = false
            } else {
              this.openMarker.justAdded = false
            }
          })
        }

        if (changeZoom) {
          if (this.ticket && !this.noLastKnownMarker()) {
            this.interactiveMap.setCamera({
              bounds: atlas.data.BoundingBox.fromLatLngs([[this.ticket.longitude, this.ticket.latitude], [this.ticket.lastKnownLongitude, this.ticket.lastKnownLatitude]]),
              padding: 150
            })
          } else if (this.isLocationMap && (this.cutLayers)) {
            this.centerCameraOnCutOrParcel()
          } else if (this.tracts) {
            this.centerVisibleTractPoints(tractsSortedByLatitude.filter(t => (this.tractId === null || this.tractId === t.tractId)))
          } else {
            this.centerVisibleTicketPoints(ticketsSortedByLatitude || [this.ticket])
          }
        } else {
          this.syncTractMarkers()
        }

        if (!this.weatherLayer) {
          this.weatherLayer = new atlas.layer.TileLayer({
            opacity: 0.9,
            tileSize: 256
          })
          if (this.interactiveMap) {
            this.interactiveMap.layers.add(this.weatherLayer, 'labels')
          }
        }

        if (!this.scaleBarRendered) {
          this.scaleBarRendered = true
          import('@/utils/azure-maps-scale-bar-control.js').then(
            sb => {
              this.scaleBarImport = sb
              this.toggleScaleBar(true)
            })
        }

        this.onResize()
      })
    },

    syncTractMarkers () {
      if (!this.interactiveMap) return
      const hidden = viewportWidthMiles(this.interactiveMap) > MAX_VIEWPORT_MILES_FOR_TRACT_MARKERS
      const bounds = this.interactiveMap.getCamera().bounds
      const inView = hidden ? [] : this.zoomGatedTracts.filter(t => atlas.data.BoundingBox.containsPosition(bounds, [t.spot.longitude, t.spot.latitude]))
      const inViewIds = new Set(inView.map(t => t.tractId))
      this.renderedTractMarkers.forEach((marker, tractId) => {
        if (inViewIds.has(tractId)) return
        if (this.openMarker.marker === marker) {
          marker.options.popup.remove()
          this.openMarker.marker = null
        }
        this.interactiveMap.markers.remove(marker)
        this.renderedTractMarkers.delete(tractId)
      })
      const entering = inView.filter(t => !this.renderedTractMarkers.has(t.tractId))
      const markers = this.createTractMarkers(entering, false)
      this.addMarkers(markers)
      markers.forEach((marker, i) => this.renderedTractMarkers.set(entering[i].tractId, marker))
      this.tractMarkersHidden = hidden && this.zoomGatedTracts.length > 0
    },

    async handleAngelGeojson () {
      if (this.isLocationMap) {
        const tract = this.tracts[0]
        const tractId = tract.tractId
        this.angelGeojson = await angelGeojsonClient.readGeojson(tractId)
        this.legend = getLegendInstance()
        if (this.angelGeojson?.layerIsNotEmpty(CUT)) {
          await this.renderCut()
          this.legend.viewingCut = true
        }
        if (this.angelGeojson?.layerIsNotEmpty(PARCELS)) {
          await this.renderParcel()
          this.legend.viewingParcel = true
        }
      }
    },

    async renderCut () {
      if (!this.cutSource) {
        this.cutSource = new atlas.source.DataSource()
        this.interactiveMap.sources.add(this.cutSource)
        this.cutSource.setShapes(this.angelGeojson.cut)
      }
      if (!this.cutLayers) {
        this.cutLayers = createPolygonLayersForDataSource(this.cutSource, PolygonStyle.Cut)
        this.interactiveMap.layers.add(this.cutLayers, 'labels')
      }
    },

    async renderParcel () {
      if (!this.parcelSource) {
        this.parcelSource = new atlas.source.DataSource()
        this.interactiveMap.sources.add(this.parcelSource)
        this.parcelSource.setShapes(this.angelGeojson.parcels)
      }
      if (!this.parcelLayers) {
        this.parcelLayers = createPolygonLayersForDataSource(this.parcelSource, PolygonStyle.Parcels)
        this.interactiveMap.layers.add(this.parcelLayers, 'labels')
      }
    },

    centerCameraOnCutOrParcel () { // Zoom to cut if it exists, otherwise zoom to parcel
      const cutShapes = this.cutSource?.getShapes()
      const parcelShapes = this.parcelSource?.getShapes()
      const point = new atlas.data.Point(new atlas.data.Position(this.tracts[0].spot.longitude, this.tracts[0].spot.latitude))
      if (cutShapes?.length > 0) {
        zoomToFeature(new atlas.data.FeatureCollection([cutShapes, point]), this.interactiveMap)
      } else if (parcelShapes?.length > 0) {
        zoomToFeature(new atlas.data.FeatureCollection([cutShapes, point]), this.interactiveMap)
      }
    },

    async getTractsToMark () {
      const tractsToMark = []
      if (this.showNearbyTracts) {
        const nearbyTracts = await this.fetchTractsOnce(new TractFilter({ includeInitiated: true, includeCruised: true, includeExecuted: true, includeClosed: true }))
        nearbyTracts.forEach(t => { tractsToMark.push(t) })
      }
      if (this.tracts) {
        this.tracts.forEach(t => { tractsToMark.push(t) })
      }
      const tractsSortedByLatitude = tractsToMark.length > 1 ? tractsToMark.sort((t1, t2) => t2.spot.latitude - t1.spot.latitude) : tractsToMark
      if (this.isLocationMap) {
        this.tract = this.tracts[0]
        this.tractId = this.tract.tractId
      }
      if (this.tractId) {
        if (this.focusedTractNotInList) {
          tractsSortedByLatitude.push(this.tract)
        } else {
          tractsSortedByLatitude
            .push(tractsSortedByLatitude
              .splice(tractsSortedByLatitude
                .indexOf(tractsSortedByLatitude
                  .filter(t => t.tractId === this.tractId)[0]), 1)[0]) // Moves focused tract so its marker appears in front
        }
      }
      return tractsSortedByLatitude
    },

    async getTicketsToMark () {
      return this.tickets
        .filter(t => t.status === TicketStatus.InTransit.value && this.transitStatusFilter.includes(t.transitStatus))
        .toSorted((t1, t2) => t2.latitude - t1.latitude)
    },

    async toggleWeatherOverlay () {
      if (this.overlayBlocked('weather', 'weather')) return
      const options = [undefined, 'radar', 'infrared']
      this.currentWeatherOverlay = options[(options.indexOf(this.currentWeatherOverlay) + 1) % options.length]
      this.weatherLayer.setOptions({
        tileUrl: this.currentWeatherOverlay ? `https://{azMapsDomain}/map/tile?api-version=2.0&tilesetId=${this.weatherLayerName}&zoom={z}&x={x}&y={y}` : undefined
      })
    },

    toggleSpeciesLayer () {
      if (this.overlayBlocked('species', 'speciesLayer')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleSpeciesLayer()
    },

    toggleTopo () {
      if (this.overlayBlocked('topo', 'topoLayer')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleTopo()
    },

    analyzeSoil () {
      if (this.overlayBlocked('soil', 'operability')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.analyzeSoilOperability()
    },

    toggleCustomLayers () {
      if (this.overlayBlocked('custom', 'customLayers')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleCustomLayersPanel()
    },

    toggleNearbyBoundaries () {
      if (this.overlayBlocked('nearbyBoundaries', 'nearbyBoundaries')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyTractBoundaries(this.tracts[0])
    },

    toggleNearbyParcels () {
      if (this.overlayBlocked('nearbyParcels', 'nearbyParcels')) return
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyParcels()
    },

    hideMapTips () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.hideDataTips()
    },

    layerActive (name) {
      switch (name) {
        case 'weather': return !!this.currentWeatherOverlay
        default: return !!this.overlayState[name]?.active // species, topo, soil, custom, nearbyBoundaries, nearbyParcels
      }
    },

    // Species and custom layers stack with each other; every other overlay is exclusive.
    otherOverlayActive (name) {
      return ['weather', 'nearbyParcels', 'species', 'topo', 'soil', 'custom', 'nearbyBoundaries'].some(o => o !== name && !overlaysStack(name, o) && this.layerActive(o))
    },

    overlayBlocked (name, layerKey) {
      if (!this.otherOverlayActive(name)) return false
      this.setSnack(this.$t('disableOtherLayersToToggle', { layer: this.$t(layerKey) }))
      return true
    },

    async toggleScaleBar (firstRender = false) {
      if (firstRender) {
        this.currentScaleBar = this.scaleBarOptions[parseInt(localStorage.getItem(LocalStorageKeys.TRACT_MAP_SCALE_BAR_TYPE))] ?? this.currentScaleBar
      } else {
        const options = this.scaleBarOptions
        this.currentScaleBar = options[(options.indexOf(this.currentScaleBar) + 1) % options.length]
        localStorage.setItem(LocalStorageKeys.TRACT_MAP_SCALE_BAR_TYPE, options.indexOf(this.currentScaleBar))
        if (this.scaleBarControl) {
          this.interactiveMap.controls.remove(this.scaleBarControl)
          this.scaleBarControl = undefined
        }
      }
      if (this.currentScaleBar) {
        this.scaleBarControl = new this.scaleBarImport.atlas.control.ScaleBarControl({ maxBarLength: 120, units: this.currentScaleBar })
        this.interactiveMap.controls.add(this.scaleBarControl)
      }
    },

    addMarkers (markers) {
      markers.forEach(marker => {
        this.interactiveMap.events.add('click', marker, () => {
          if (this.openMarker.marker?.options.popup) {
            this.openMarker.marker.options.popup.remove()
          }
          this.openMarker = {
            marker: marker,
            justAdded: true
          }
          marker.togglePopup()
        })

        this.interactiveMap.markers.add(marker)
      })
    },

    createTractMarkers (tractsSorted, animateSearched = true) {
      const tractMarkers = []
      tractsSorted.forEach(tract => {
        const coordinate = [tract.spot.longitude, tract.spot.latitude]
        const progress = harvestedProgress(tract)
        const focused = tract.tractId === this.tractId
        const onDetail = this.includeTractDetailLink && !(this.isLocationMap && focused) ? () => this.$emit('tract-detail', tract) : undefined
        const popup = tractPopup(tract, progress, focused, onDetail)
        const markerSVG = tractMarkerSvg(tract, progress)
        const htmlContent = tract.tractId === this.tractId && animateSearched
          ? `<div class="important-tract bounce"> ${markerSVG} </div><div class="${tract.type.category !== 1 ? 'stumpage' : 'delivered'}"/>`
          : tract.tractId === this.tractId
            ? `<div class="important-tract"> ${markerSVG} </div>`
            : markerSVG

        const markerOptions = {
          htmlContent,
          position: coordinate,
          popup
        }
        tractMarkers.push(new atlas.HtmlMarker(markerOptions))
      })
      return tractMarkers
    },

    createTicketMarkers (ticketsSorted = undefined) {
      const ticketMarkers = []
      if (ticketsSorted) {
        ticketsSorted.forEach(ticket => {
          const coordinate = [ticket.longitude, ticket.latitude]
          const popup = this.getTicketPopup(ticket)
          const markerSVG = mapMarkers.load(this.getColorByTransitStatus(ticket.transitStatus))
          const htmlContent = markerSVG

          const markerOptions = {
            htmlContent,
            position: coordinate,
            popup
          }
          ticketMarkers.push(new atlas.HtmlMarker(markerOptions))
        })
      } else {
        ticketMarkers.push(new atlas.HtmlMarker({
          position: [this.ticket.longitude, this.ticket.latitude],
          htmlContent: this.noLastKnownMarker(this.ticket) ? mapMarkers.load('#F9F6DF') : mapMarkers.home,
          popup: this.getTicketPopup(this.ticket)
        }))
        if (!this.noLastKnownMarker(this.ticket)) {
          ticketMarkers.push(new atlas.HtmlMarker({
            position: [this.ticket.lastKnownLongitude, this.ticket.lastKnownLatitude],
            htmlContent: mapMarkers.load('#F9F6DF'),
            popup: this.getTicketPopup(this.ticket)
          }))
        }
      }

      return ticketMarkers
    },

    async toggleAccountLayer () {
      this.showingAccountLayer = !this.showingAccountLayer
      if (this.showingAccountLayer && this.accountLocations.length === 0) {
        this.accountLocations = accountsWithReceiverLocation(await this.fetchAccounts())
      }
      this.renderAccountMarkers()
    },

    renderAccountMarkers () {
      this.accountMarkers.forEach(marker => {
        marker.options.popup.remove()
        this.interactiveMap.markers.remove(marker)
      })
      this.accountMarkers = []
      if (!this.showingAccountLayer) return
      const origin = { latitude: this.tract?.spot?.latitude, longitude: this.tract?.spot?.longitude }
      this.accountMarkers = this.accountLocations.map(account => {
        const miles = milesBetween(origin, { latitude: account.receiverSpotLatitude, longitude: account.receiverSpotLongitude })
        return createAccountLocationMarker(
          account,
          account.isExternal ? this.$t('external') : this.$t('internal'),
          miles !== undefined ? this.$t('milesShort', { distance: miles.toFixed(1) }) : undefined)
      })
      this.addMarkers(this.accountMarkers)
    },

    centerVisibleTractPoints (tracts) {
      const targetOptions = (tracts.length > 1) ? {
        bounds: atlas.data.BoundingBox.fromPositions(tracts
          .map(t => new atlas.data.Position(t.spot.longitude, t.spot.latitude, 0))),
        padding: 150
      } : tracts.length > 0 ? {
        center: [tracts[0].spot.longitude, tracts[0].spot.latitude],
        zoom: 14
      } : this.ticket ? {
        center: [this.ticket.longitude, this.ticket.latitude],
        zoom: 14
      } : {
        center: defaultCenterCoords,
        zoom: 3
      }

      this.interactiveMap.setCamera({
        ...targetOptions,
        type: 'fly',
        duration: 500
      })
    },

    centerVisibleTicketPoints (tickets) {
      const targetOptions = (tickets.length > 1) ? {
        bounds: atlas.data.BoundingBox.fromPositions(tickets
          .map(t => new atlas.data.Position(t.longitude, t.latitude, 0))),
        padding: 150
      } : tickets.length > 0 ? {
        center: [tickets[0].longitude, tickets[0].latitude],
        zoom: 14
      } : this.ticket ? {
        center: [this.ticket.longitude, this.ticket.latitude],
        zoom: 14
      } : {
        center: defaultCenterCoords,
        zoom: 3
      }

      this.interactiveMap.setCamera({
        ...targetOptions,
        type: 'fly',
        duration: 500
      })
    },

    getTicketPopup (ticket) {
      const css =
        'color: #F9F6DF; font-weight: bold; font-size: 20px; padding: 7px;'

      const tractHtml = ticket.tract ? `<span class="mdi mdi-crosshairs-gps map-marker-popup-line"/> <span class="map-marker-popup-line">${ticket.tract}</span> <br>` : ''
      const fromAccountHtml = ticket.fromAccount ? `<span class="mdi mdi-account-arrow-right map-marker-popup-line"/> <span class="map-marker-popup-line">${ticket.fromAccount}</span> <br>` : ''

      const interiorHtml = this.ticket
        ? `
        <span id="map-marker-popup-name">#${ticket.ticketNumber}</span>
        <hr class="map-marker-popup-divider">
        ${fromAccountHtml}
        ${tractHtml}
        <span class="mdi ${ticket.isExternal ? 'mdi-navigation' : 'mdi-home'} map-marker-popup-line"/>
        <span class="map-marker-popup-line">${ticket.destination}</span> 
        <br>
        <span class="mdi mdi-pine-tree-variant map-marker-popup-line"/> 
        <span class="map-marker-popup-line">${ticket.product}</span> 
        <br>
        <span class="map-marker-popup-line" style="font-style:italic;font-weight:300;">Created ${utcToLocalDate(ticket.loadCreatedAt)}</span>
        <div class="ticket-popup-tail"></div>
        `
        : `
        <div style="display: flex; justify-content: space-between;">
          <div>
            <span id="map-marker-popup-name">#${ticket.ticketNumber}</span>
          </div>
          <div class="map-popup-spacer"></div>
          <div>
            <span class="map-marker-popup-trailer-id">${ticket.trailerIdentifier}</span>
            <span class="mdi mdi-truck map-marker-popup-trailer-id"/> 
          </div>
        </div>
        <hr class="map-marker-popup-divider">
        <span class="map-marker-popup-transit-status" style="color:${this.getColorByTransitStatus(ticket.transitStatus)};">${this.$t(this.transitStatus.find(ts => ts.value === ticket.transitStatus).label)}</span> 
        <br>
        ${fromAccountHtml}
        ${tractHtml}
        <br>
        <span class="mdi ${ticket.isExternal ? 'mdi-navigation' : 'mdi-home'} map-marker-popup-line"/>
        <span class="map-marker-popup-line">${ticket.destination}</span> 
        <br>
        <span class="mdi mdi-pine-tree-variant map-marker-popup-line"/> 
        <span class="map-marker-popup-line">${ticket.product}</span> 
        <br>
        <span class="map-marker-popup-line" style="font-style:italic;font-weight:300;">Created ${utcToLocalDate(ticket.loadCreatedAt)}</span>
        <div class="ticket-popup-tail"></div>
        `
      const popupOptions = {
        content: `<div style='${css}'>${interiorHtml}</div>`,
        pixelOffset: [0, -35],
        closeButton: false,
        showPointer: true
      }

      return new atlas.Popup(popupOptions)
    },

    tractChosen (val) {
      if (val) {
        this.tractId = val.tractId
        this.tract = val
      }
    },

    tractCleared () {
      this.tractId = null
      this.tract = null
    },

    applyFilter (filter) {
      this.$emit('filter-applied', filter)
    },

    onResize () {
      if (this.interactiveMap) {
        setTimeout(() => this.interactiveMap.map.resize(), mapResizeTimeout)
      }
    },

    noLastKnownMarker () {
      const ticket = this.ticket
      const imagesOrWeights = ticket.hasImages || ticket.status !== TicketStatus.InTransit.value
      const noLastKnownLocation = ticket.latitude === ticket.lastKnownLatitude && ticket.longitude === ticket.lastKnownLongitude
      return noLastKnownLocation || imagesOrWeights
    },

    getCheckboxLabel (name, value) {
      return `${this.$t(name)} (${this.tickets.filter(t => t.transitStatus === value && t.status === TicketStatus.InTransit.value).length})`
    },

    getColorByTransitStatus (status) {
      return status === TransitStatus.Ready.value ? '#44e94b' : '#f55a4e'
    },

    toggleLegendItemVisibility (legendItem) {
      legendItem.hidden = !legendItem.hidden
      let layers
      switch (legendItem.label) {
        case 'parcel': {
          layers = this.parcelLayers
          break
        }
        case 'tractCut': {
          layers = this.cutLayers
        }
      }
      layers.forEach(layer => this.setLayerVisibility(layer, !legendItem.hidden))
    },

    setLayerVisibility (layer, isVisible) {
      const options = layer.getOptions()
      options.visible = isVisible
      layer.setOptions(options)
    },

    async copyLastKnownLocationToClipboard () {
      if (this.ticket && this.ticket.lastKnownLatitude && this.ticket.lastKnownLongitude) {
        const coords = `${this.ticket.lastKnownLatitude.toFixed(6)}, ${this.ticket.lastKnownLongitude.toFixed(6)}`
        await navigator.clipboard.writeText(coords)
        this.setSnack(this.$t('copiedToClipboard', { content: coords }))
      }
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
